import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router";

import { AuthProvider } from "@/components/auth-provider";
import { AppErrorBoundary } from "@/components/error-boundary";
import { AppQueryProvider } from "@/components/query-provider";
import { Spinner } from "@/components/ui/spinner";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppThemeProvider } from "@/components/theme-provider";

const ActivityRoute = lazy(() => import("@/pages/activity"));
const AccountRoute = lazy(() => import("@/pages/account"));
const AdminUsersRoute = lazy(() => import("@/pages/admin-users"));
const DataRoute = lazy(() => import("@/pages/data"));
const HelpRoute = lazy(() => import("@/pages/help"));
const LoginRoute = lazy(() => import("@/pages/login"));
const NewStudyRoute = lazy(() => import("@/pages/studies-new"));
const NotFoundRoute = lazy(() => import("@/pages/not-found"));
const RegisterRoute = lazy(() => import("@/pages/register"));
const SettingsRoute = lazy(() => import("@/pages/settings"));
const ShareRoute = lazy(() => import("@/pages/share"));
const SignInRoute = lazy(() => import("@/pages/signin"));
const StudiesRoute = lazy(() => import("@/pages/studies"));
const TaskRoute = lazy(() => import("@/pages/task"));
const WorkspaceIndexRoute = lazy(() => import("@/pages/workspace-index"));
const WorkspaceRoute = lazy(() => import("@/pages/workspace"));
const WorkspacesRoute = lazy(() => import("@/pages/workspaces"));

export function App() {
  return (
    <AuthProvider>
      <AppThemeProvider>
        <AppQueryProvider>
          <TooltipProvider>
            <AppErrorBoundary>
              <Suspense fallback={<RouteFallback />}>
                <Routes>
                  <Route path="/" element={<Navigate replace to="/data" />} />
                  <Route path="/data" element={<DataRoute />} />
                  <Route path="/activity" element={<ActivityRoute />} />
                  <Route path="/studies" element={<StudiesRoute />} />
                  <Route path="/studies/new" element={<NewStudyRoute />} />
                  <Route path="/workspaces" element={<WorkspacesRoute />} />
                  <Route path="/workspace" element={<WorkspaceIndexRoute />} />
                  <Route path="/workspace/:studyId" element={<WorkspaceRoute />} />
                  <Route path="/task/:taskId" element={<TaskRoute />} />
                  <Route path="/share/:studyId" element={<ShareRoute />} />
                  <Route path="/auth/login" element={<LoginRoute />} />
                  <Route path="/auth/register" element={<RegisterRoute />} />
                  <Route path="/signin" element={<SignInRoute />} />
                  <Route path="/admin" element={<Navigate replace to="/admin/users" />} />
                  <Route path="/admin/users" element={<AdminUsersRoute />} />
                  <Route path="/settings" element={<SettingsRoute />} />
                  <Route path="/help" element={<HelpRoute />} />
                  <Route path="/account" element={<AccountRoute />} />
                  <Route path="*" element={<NotFoundRoute />} />
                </Routes>
              </Suspense>
            </AppErrorBoundary>
          </TooltipProvider>
          <Toaster position="top-center" />
        </AppQueryProvider>
      </AppThemeProvider>
    </AuthProvider>
  );
}

function RouteFallback() {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <Spinner className="size-6" />
    </div>
  );
}
